import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { submitApplication } from "./submitApplication";
import { supabase } from "../../supabaseClient";
import {
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  CalendarDays,
  CheckCircle,
  Clock,
  MapPin,
  MessageCircle,
  RefreshCw,
} from "lucide-react";
import { formatEventDate, formatDeadline } from "./vendorDashboard.utils";
import "./VendorDashboardHome.css"; // shared tokens and buttons
import "./VendorUpdatePage.css";

const FALLBACK_IMAGE = "/vendor/placeholder-update.jpg";

/** Used when an update accepts applications but defines no custom fields. */
const DEFAULT_FIELDS = [
  { key: "message", label: "Anything we should know?", type: "textarea", required: false },
];

/* -------------------------------------------------------------------------- */
/* Form Components & Helpers                                                 */
/* -------------------------------------------------------------------------- */

const initialAnswers = (fields) =>
  Object.fromEntries(fields.map((f) => [f.key, f.type === "checkbox" ? false : ""]));

const validate = (fields, answers) => {
  const errors = {};
  fields.forEach((f) => {
    if (!f.required) return;
    const value = answers[f.key];
    const empty = f.type === "checkbox" ? !value : String(value ?? "").trim() === "";
    if (empty) errors[f.key] = f.type === "checkbox" ? "This needs to be ticked." : "This field is required.";
  });
  return errors;
};

const Field = ({ field, value, error, onChange }) => {
  const id = `vu-${field.key}`;
  const shared = {
    id,
    name: field.key,
    required: field.required,
    "aria-invalid": Boolean(error),
    "aria-describedby": error ? `${id}-error` : undefined,
  };

  let control;
  if (field.type === "textarea") {
    control = (
      <textarea
        {...shared}
        rows={4}
        className="vu-input"
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
    );
  } else if (field.type === "select") {
    control = (
      <select {...shared} className="vu-input" value={value} onChange={(e) => onChange(field.key, e.target.value)}>
        <option value="">Select an option</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "checkbox") {
    return (
      <div className="vu-field vu-field--check">
        <label htmlFor={id} className="vu-check">
          <input
            {...shared}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(field.key, e.target.checked)}
          />
          <span>{field.label}</span>
        </label>
        {error && (
          <p id={`${id}-error`} className="vu-field-error">
            {error}
          </p>
        )}
      </div>
    );
  } else {
    control = (
      <input
        {...shared}
        type={["email", "tel", "number", "url", "date"].includes(field.type) ? field.type : "text"}
        className="vu-input"
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
    );
  }

  return (
    <div className="vu-field">
      <label htmlFor={id} className="vu-label">
        {field.label}
        {!field.required && <span className="vu-optional"> (optional)</span>}
      </label>
      {field.help && <p className="vu-help">{field.help}</p>}
      {control}
      {error && (
        <p id={`${id}-error`} className="vu-field-error">
          {error}
        </p>
      )}
    </div>
  );
};

const ApplyForm = ({ update, vendor, onApplied }) => {
  const fields = useMemo(
    () => (Array.isArray(update.form_fields) && update.form_fields.length ? update.form_fields : DEFAULT_FIELDS),
    [update.form_fields]
  );
  const [answers, setAnswers] = useState(() => initialAnswers(fields));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate(fields, answers);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const saved = await submitApplication(update.id, vendor?.id, answers);
      setSubmitting(false);
      onApplied(saved || { created_at: new Date().toISOString() });
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err.message);
    }
  };

  return (
    <form className="vu-form" onSubmit={handleSubmit} noValidate>
      {fields.map((field) => (
        <Field key={field.key} field={field} value={answers[field.key]} error={errors[field.key]} onChange={setAnswer} />
      ))}
      {submitError && (
        <p className="vu-submit-error" role="alert">
          <AlertCircle size={15} aria-hidden="true" />
          {submitError}
        </p>
      )}
      <button type="submit" className="vh-btn vh-btn--primary vu-submit" disabled={submitting}>
        {submitting ? "Sending…" : update.apply_label || "Submit application"}
      </button>
    </form>
  );
};

/* -------------------------------------------------------------------------- */
/* Page Sections                                                             */
/* -------------------------------------------------------------------------- */

const Fact = ({ icon: Icon, label, children }) => (
  <div className="vu-fact">
    <Icon size={16} aria-hidden="true" />
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  </div>
);

const ApplyPanel = ({ update, vendor, application, onApplied }) => {
  const deadline = update.apply_deadline ? new Date(update.apply_deadline) : null;
  const closed = deadline && deadline < new Date();
  const hasCustomForm = Array.isArray(update.form_fields) && update.form_fields.length > 0;

  let body;
  if (application) {
    body = (
      <div className="vu-state vu-state--done" role="status">
        <CheckCircle size={20} aria-hidden="true" />
        <div>
          <p className="vu-state-title">Application received</p>
          <p className="vu-state-text">
            You applied on {formatEventDate(application.created_at)}. We'll be in touch with a decision.
          </p>
        </div>
      </div>
    );
  } else if (closed) {
    body = (
      <div className="vu-state">
        <Clock size={20} aria-hidden="true" />
        <div>
          <p className="vu-state-title">Applications closed</p>
          <p className="vu-state-text">This opportunity stopped taking applications on {formatDeadline(update.apply_deadline)}.</p>
        </div>
      </div>
    );
  } else if (!hasCustomForm && update.link) {
    body = (
      <a href={update.link} className="vh-btn vh-btn--primary vu-submit" target="_blank" rel="noopener noreferrer">
        <MessageCircle size={16} aria-hidden="true" />
        Apply on WhatsApp
      </a>
    );
  } else {
    body = <ApplyForm update={update} vendor={vendor} onApplied={onApplied} />;
  }

  return (
    <section className="vu-apply" aria-labelledby="vu-apply-title">
      <h2 id="vu-apply-title" className="vu-apply-title">
        {application ? "Your application" : "Apply"}
      </h2>
      {deadline && !closed && !application && (
        <p className="vu-apply-note">Applications close {formatDeadline(update.apply_deadline)}.</p>
      )}
      {body}
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/* Primary Component                                                          */
/* -------------------------------------------------------------------------- */

const VendorUpdatePage = () => {
  const { updateId } = useParams();
  const { vendor } = useOutletContext() || {};
  const [update, setUpdate] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      const [updateRes, appRes] = await Promise.all([
        supabase.from("vendor_updates").select("*").eq("id", updateId).eq("is_published", true).maybeSingle(),
        vendor?.id
          ? supabase
              .from("vendor_update_applications")
              .select("id, created_at")
              .eq("update_id", updateId)
              .eq("vendor_id", vendor.id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (cancelled) return;

      if (updateRes.error) {
        console.error("Failed to load update:", updateRes.error);
        setError("We couldn't load this page. Check your connection and try again.");
      } else {
        setUpdate(updateRes.data);
        if (appRes.error) console.error("Failed to load application:", appRes.error);
        setApplication(appRes.error ? null : appRes.data);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [updateId, vendor?.id, reloadToken]);

  if (loading) {
    return (
      <div className="vh-loading" role="status">
        <div className="vh-spinner" aria-hidden="true" />
        <span>Loading…</span>
      </div>
    );
  }

  const back = (
    <Link to="/vendor" className="vu-back">
      <ArrowLeft size={15} aria-hidden="true" />
      Back to dashboard
    </Link>
  );

  if (error) {
    return (
      <div className="vh-page vu-page">
        {back}
        <div className="vh-error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
          <button type="button" className="vh-error-retry" onClick={() => setReloadToken((n) => n + 1)}>
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!update) {
    return (
      <div className="vh-page vu-page">
        {back}
        <div className="vu-missing">
          <h1 className="vu-title">This page isn't available</h1>
          <p>It may have been unpublished or the link may be wrong.</p>
          <Link to="/vendor" className="vh-btn vh-btn--primary">
            Go to dashboard
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="vh-page vu-page">
      {back}

      <img
        src={update.image_url || FALLBACK_IMAGE}
        alt=""
        className="vu-hero"
        onError={(e) => {
          if (!e.currentTarget.src.endsWith(FALLBACK_IMAGE)) e.currentTarget.src = FALLBACK_IMAGE;
        }}
      />

      <div className="vu-layout">
        <article className="vu-content">
          <span className="vu-type">{update.type}</span>
          <h1 className="vu-title">{update.title}</h1>
          {update.description && update.body && <p className="vu-lede">{update.description}</p>}
          
          <div className="vu-prose">
            <ReactMarkdown>{update.body || update.description || ""}</ReactMarkdown>
          </div>
        </article>

        <aside className="vu-side">
          <dl className="vu-facts">
            {update.start_date && (
              <Fact icon={CalendarDays} label="When">
                {formatEventDate(update.start_date)}
                {update.end_date ? ` to ${formatEventDate(update.end_date)}` : ""}
              </Fact>
            )}
            {update.location && (
              <Fact icon={MapPin} label="Where">
                {update.location}
              </Fact>
            )}
            {update.apply_deadline && (
              <Fact icon={Clock} label="Apply by">
                {formatDeadline(update.apply_deadline)}
              </Fact>
            )}
          </dl>

          {update.apply_enabled && (
            <ApplyPanel update={update} vendor={vendor} application={application} onApplied={setApplication} />
          )}
        </aside>
      </div>
    </div>
  );
};

export default VendorUpdatePage;